import { useMutation } from '@tanstack/react-query';

import { useTRPC } from '@/utils/trpc';

type BaseFeedbackData = {
  spanId: string;
  traceId: string;
  app: string;
  isPositiveFeedback: boolean;
};

export type WorkflowFeedbackData = {
  workflowName: string;
  primaryReason?: string;
  additionalComment?: string;
} & BaseFeedbackData;

export type AIAssistantFeedbackData = {
  userQuery?: string;
  aiResponse?: string;
  sessionId: string;
  responseId: string;
  primaryReason?: string;
  additionalComment?: string;
} & BaseFeedbackData;

export type FeedbackData = WorkflowFeedbackData | AIAssistantFeedbackData;

function isWorkflowFeedback(
  feedback: FeedbackData
): feedback is WorkflowFeedbackData {
  return (feedback as WorkflowFeedbackData).workflowName !== undefined;
}

function isAIAssistantFeedback(
  feedback: FeedbackData
): feedback is AIAssistantFeedbackData {
  return (feedback as AIAssistantFeedbackData).sessionId !== undefined;
}

function isPositiveToFeedbackType(
  isPositiveFeedback: boolean
): 'thumbs_up' | 'thumbs_down' {
  return isPositiveFeedback ? 'thumbs_up' : 'thumbs_down';
}

export function useAIFeedbackService() {
  const trpc = useTRPC();

  const aiAssistantMutation = useMutation({
    ...trpc.frontend.aiFeedback.submitAiAssistantFeedback.mutationOptions({}),
    throwOnError: false,
  });

  const workflowMutation = useMutation({
    ...trpc.frontend.aiFeedback.submitWorkflowFeedback.mutationOptions({}),
    throwOnError: false,
  });

  return {
    submitFeedback: async (feedback: FeedbackData): Promise<void> => {
      if (isWorkflowFeedback(feedback)) {
        await workflowMutation.mutateAsync({
          spanId: feedback.spanId,
          traceId: feedback.traceId,
          app: feedback.app,
          feedbackType: isPositiveToFeedbackType(feedback.isPositiveFeedback),
          primaryReason: feedback.primaryReason,
          additionalComment: feedback.additionalComment,
          workflowName: feedback.workflowName,
        });
      } else if (isAIAssistantFeedback(feedback)) {
        await aiAssistantMutation.mutateAsync({
          spanId: feedback.spanId,
          traceId: feedback.traceId,
          app: feedback.app,
          feedbackType: isPositiveToFeedbackType(feedback.isPositiveFeedback),
          primaryReason: feedback.primaryReason,
          additionalComment: feedback.additionalComment,
          userQuery: feedback.userQuery,
          aiResponse: feedback.aiResponse,
          sessionId: feedback.sessionId,
          responseId: feedback.responseId,
        });
      }
    },
    loading: aiAssistantMutation.isPending || workflowMutation.isPending,
    error: aiAssistantMutation.error || workflowMutation.error,
  };
}
